import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { applyPageMetadata } from '../utils/seo';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminChecking, setAdminChecking] = useState(false);
  const [error, setError] = useState('');
  
  const { login, logout } = useAuth();

  useEffect(() => {
    const cleanup = applyPageMetadata({
      title: 'Bartender Login | BarsConnect',
      description: 'Log in to your bartender account to manage your profile, discover shifts, and connect with hiring bars.',
      keywords: 'bartender login, barsconnect login, bartender account access',
      canonicalUrl: 'https://barsconnect.com/login',
      robots: 'index,follow',
    });

    return cleanup;
  }, []);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Redirect based on user role
        if (result.user?.is_admin) {
          window.location.href = '/admin/dashboard';
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminAccessCheck = async () => {
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password to verify admin access');
      return;
    }

    setAdminChecking(true);
    try {
      const result = await login(email, password);
      if (!result.success) {
        setError(result.error || 'Admin access check failed');
        return;
      }

      if (!result.user?.is_admin) {
        logout();
        setError('This account does not have admin access');
        return;
      }

      toast.success('Admin access verified');
      window.location.href = '/admin/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Admin access check failed');
    } finally {
      setAdminChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary font-['Barlow_Condensed'] uppercase tracking-tight">
            BARSCONNECT
          </h1>
          <p className="text-muted-foreground mt-2">
            Welcome back to your bartending network
          </p>
        </div>

        <Card className="bg-white/5 backdrop-blur-md border border-white/5 shadow-2xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center font-['Barlow_Condensed'] uppercase">
              Sign In
            </CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div 
                  className="p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] h-12 px-8 rounded-md font-bold uppercase tracking-wide transition-all shadow-[0_0_20px_-5px_rgba(255,45,138,0.5)] hover:shadow-[0_0_30px_-5px_rgba(255,45,138,0.7)]"
                disabled={loading || adminChecking}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              <div className="w-full flex items-center gap-2" data-testid="login-admin-divider">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-xs text-muted-foreground">Admin Tools</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleAdminAccessCheck}
                disabled={loading || adminChecking}
                className="w-full border-primary/30 text-primary hover:bg-primary/10"
                data-testid="admin-access-check-btn"
              >
                {adminChecking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying Admin Access...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Admin Access Check
                  </>
                )}
              </Button>
              
              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{' '}
                <Link 
                  to="/register" 
                  className="font-medium text-primary hover:underline"
                  data-testid="register-link"
                >
                  Create account
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
